/**
 * Die Eingabe einer Zahlungsstaffel in Worte gefasst (Z1.3, `docs/KONZEPT-ZAHLUNGSPLAN.md` B-1).
 *
 * WAS HIER PASSIERT: Aus den Feldern der Eingabemaske („50", „Prozent", „2026-09-01") wird die
 * Nutzlast, die die Route erwartet — und ein Satz, der dem Betreiber sagt, was seine Stufen
 * ergeben und woran es hakt.
 *
 * WAS HIER NICHT PASSIERT: gerechnet und geurteilt. Die Betraege je Stufe kommen aus
 * `stufeSollCents`, das Urteil ueber die Staffel als Ganzes aus `berechneStaffel`, die Pruefung
 * der Reihenfolge aus `ersteRueckwaertsStufe` — alle drei in `@maestro/pricing`, derselben Datei,
 * die auch die Route benutzt. Genau deshalb kann die Maske nichts durchlassen, was die Route
 * abweist, und nichts anzeigen, was spaeter anders in der Datenbank steht. Die Ratsche
 * `apps/api/test/staffel-eine-rechenstelle.test.ts` wird rot, sobald hier doch gerechnet wird.
 *
 * WARUM die Meldungen nicht die des Servers sind: `berechneStaffel` liegt in einem Paket, das
 * ohne Umlaute geschrieben ist („laesst sich nicht rechnen"). Das ist im Quelltext richtig und
 * im Fenster eines Betreibers falsch. Uebernommen wird deshalb der FEHLERCODE, formuliert wird
 * hier — dieselbe Arbeitsteilung wie bei `zahlungsdeckung-anzeige.ts`.
 */
import { berechneStaffel, ersteRueckwaertsStufe, stufeSollCents } from "@maestro/pricing";
import { money, parseGermanDecimal, parseGermanEurosToCents } from "../ui/format";

/** Eine Zeile der Eingabemaske — Rohtext, so wie er in den Feldern steht. */
export interface StaffelEingabe {
  art: "prozent" | "betrag";
  /** Prozentsatz oder Euro-Betrag als deutsche Eingabe („33,33" · „1.500,00"). */
  wert: string;
  /** Faelligkeit als ISO-Tagesdatum (YYYY-MM-DD), wie das Datumsfeld sie liefert. */
  faellig: string;
}

/** Was die Route unter `stufen` erwartet — jede Stufe mit eigener, sichtbarer Faelligkeit. */
export interface StaffelNutzlast {
  prozentBp?: number;
  betragCents?: number;
  dueDate: string;
}

export interface StaffelPruefung {
  /** Absendbar? Nur dann ist `nutzlast` gesetzt. */
  gueltig: boolean;
  nutzlast: StaffelNutzlast[] | null;
  /** Betrag je Stufe fuer die Vorschau; `null` an Stellen, deren Eingabe (noch) unlesbar ist. */
  betraegeCents: Array<number | null>;
  /** Summe der lesbaren Stufen — die laufende Anzeige unter der Liste. */
  summeCents: number;
  /** Ein Satz: was die Stufen ergeben, oder was fehlt. */
  meldung: string;
}

/** Eine Stufe, wie sie die Rechenstelle nimmt — genau eines der beiden Felder ist gesetzt. */
type StaffelWert = { prozentBp: number } | { betragCents: number };

const MAX_STUFEN = 24;

/**
 * Der Fehlercode aus `berechneStaffel` in einem Satz, den ein Betreiber versteht.
 * `stufe` ist der 0-basierte Index aus dem Ergebnis; angezeigt wird er als Stufennummer.
 */
function fehlersatz(fehler: string, stufe: number | undefined, angebotssummeCents: number): string {
  const nr = stufe === undefined ? null : stufe + 1;
  switch (fehler) {
    case "keine_stufen":
      return "Eine Staffel braucht mindestens eine Stufe.";
    case "keine_angebotssumme":
      return "Ohne Angebotssumme lässt sich keine Staffel anlegen – zuerst eine Angebotsvariante wählen.";
    case "stufe_unklar":
      return `Stufe ${nr}: Bitte entweder einen Prozentsatz oder einen Betrag angeben.`;
    case "prozent_ungueltig":
      return `Stufe ${nr}: Der Prozentsatz muss über 0 und höchstens 100 liegen.`;
    case "betrag_ungueltig":
      return `Stufe ${nr}: Der Betrag muss über 0,00 € liegen.`;
    case "prozent_zu_hoch":
      return "Die Stufen ergeben zusammen mehr als 100 % der Angebotssumme.";
    case "betrag_zu_gross":
      return `Die festen Beträge überschreiten die Angebotssumme von ${money(angebotssummeCents)}.`;
    case "stufe_leer":
      return `Für Stufe ${nr} bleibt nichts übrig – die vorherigen Stufen schöpfen die Angebotssumme bereits aus.`;
    case "rest_zu_gross":
      return `Die Stufen ergeben nicht die Angebotssumme von ${money(angebotssummeCents)} – bitte die Anteile anpassen.`;
    default:
      return "Die Staffel ist so nicht gültig.";
  }
}

/**
 * Prueft die Eingabe und formuliert das Ergebnis.
 *
 * Reihenfolge der Pruefungen ist Absicht: erst die eigene Zeile (was steht da ueberhaupt?), dann
 * die Staffel als Ganzes (deckt sie die Summe?), zuletzt die Abfolge der Faelligkeiten. So nennt
 * die Meldung immer das, was der Betreiber als Naechstes anfassen muss — und nicht eine
 * Folgewirkung davon.
 */
export function pruefeStaffel(angebotssummeCents: number | null | undefined, zeilen: readonly StaffelEingabe[]): StaffelPruefung {
  const leer: StaffelPruefung = { gueltig: false, nutzlast: null, betraegeCents: zeilen.map(() => null), summeCents: 0, meldung: "" };
  if (!angebotssummeCents || angebotssummeCents <= 0) {
    return { ...leer, meldung: fehlersatz("keine_angebotssumme", undefined, 0) };
  }
  if (zeilen.length === 0) return { ...leer, meldung: fehlersatz("keine_stufen", undefined, angebotssummeCents) };
  if (zeilen.length > MAX_STUFEN) {
    return { ...leer, meldung: `Mehr als ${MAX_STUFEN} Stufen sind nicht vorgesehen.` };
  }

  // Schritt 1: Rohtext -> Stufe. Was hier nicht lesbar ist, bleibt `null` und macht die Staffel
  // unvollstaendig — die Vorschau zeigt trotzdem, was die uebrigen Stufen ergeben.
  const stufen: Array<StaffelWert | null> = zeilen.map((z) => {
    if (z.art === "prozent") {
      const p = parseGermanDecimal(z.wert);
      if (p === null || p <= 0) return null;
      return { prozentBp: Math.round(p * 100) };
    }
    const cents = parseGermanEurosToCents(z.wert);
    if (cents === null || cents <= 0) return null;
    return { betragCents: cents };
  });
  const betraegeCents = stufen.map((s) => (s === null ? null : stufeSollCents(angebotssummeCents, s)));
  const summeCents = betraegeCents.reduce<number>((s, b) => s + (b ?? 0), 0);
  const stand = { betraegeCents, summeCents };

  const unlesbar = stufen.findIndex((s, i) => s === null || betraegeCents[i] === null);
  if (unlesbar >= 0) {
    const zeile = zeilen[unlesbar] as StaffelEingabe;
    return {
      ...leer,
      ...stand,
      meldung:
        zeile.art === "prozent"
          ? `Stufe ${unlesbar + 1}: Bitte einen Prozentsatz eintragen (zum Beispiel 30).`
          : `Stufe ${unlesbar + 1}: Bitte einen Betrag in Euro eintragen (zum Beispiel 1.500,00).`,
    };
  }

  // Schritt 2: das Urteil ueber die Staffel — ausschliesslich aus der gemeinsamen Rechenstelle.
  const vollstaendig = stufen as StaffelWert[];
  const ergebnis = berechneStaffel(angebotssummeCents, vollstaendig);
  if (!ergebnis.ok) {
    return { ...leer, ...stand, meldung: fehlersatz(ergebnis.fehler, ergebnis.stufe, angebotssummeCents) };
  }

  // Schritt 3: jede Stufe braucht eine sichtbare Faelligkeit (M-11), und die Abfolge muss stimmen.
  const ohneDatum = zeilen.findIndex((z) => !z.faellig);
  if (ohneDatum >= 0) {
    return { ...leer, ...stand, meldung: `Stufe ${ohneDatum + 1}: Bitte eine Fälligkeit wählen – ohne Datum fiele die Stufe aus dem Mahnwesen.` };
  }
  const rueckwaerts = ersteRueckwaertsStufe(zeilen.map((z) => z.faellig));
  if (rueckwaerts !== null) {
    return {
      ...leer,
      ...stand,
      meldung: `Stufe ${rueckwaerts + 1} ist früher fällig als eine vorherige Stufe – die Stufen einer Staffel werden nacheinander fällig.`,
    };
  }

  // Die angezeigten Betraege sind ab hier die verbindlichen: `berechneStaffel` hat den
  // Rundungsausgleich in die letzte Stufe gelegt, genau wie die Route es gleich tun wird.
  const endgueltig = ergebnis.betraegeCents;
  return {
    gueltig: true,
    nutzlast: vollstaendig.map((s, i) => ({ ...s, dueDate: (zeilen[i] as StaffelEingabe).faellig })),
    betraegeCents: endgueltig,
    summeCents: endgueltig.reduce((a, b) => a + b, 0),
    meldung: `Die Stufen ergeben ${money(angebotssummeCents)} – genau die Angebotssumme.`,
  };
}
